import type { DragEvent } from 'react';
import type { DropPoint } from './useEditorCanvas';

/**
 * Dragging something out of Assets and letting go of it on the canvas.
 *
 * What is carried is the tile's own way of putting itself on the page, held here while the
 * drag lasts: the drag's data names only that something of ours is coming, because a picture,
 * a clip, a template and a line of type each place themselves differently, and a browser drag
 * can carry nothing but strings. The canvas asks for it where it is let go (`dropCarried`).
 */
const CARRIED = 'application/x-flobi-asset';

let carried: ((at: DropPoint) => void) | null = null;

/** Spread onto a tile: pressing it still places it; dragging it places it where it is let go. */
export function carry(place: (at: DropPoint, tile: HTMLElement) => void) {
  return {
    draggable: true,
    onDragStart: (event: DragEvent<HTMLElement>) => {
      const tile = event.currentTarget;
      carried = (at) => place(at, tile);
      event.dataTransfer.setData(CARRIED, '1');
      event.dataTransfer.effectAllowed = 'copy';
    },
    // After a drop the canvas has taken it already; after a drag let go anywhere else, it is put down.
    onDragEnd: () => {
      carried = null;
    },
  };
}

/** Whether a drag is one of ours, by the kinds of data it says it has (all a drag over the canvas may read). */
export function isCarried(types: readonly string[]): boolean {
  return types.includes(CARRIED);
}

/** Places what is carried at `at`. False when nothing was. */
export function dropCarried(at: DropPoint): boolean {
  const place = carried;
  carried = null;
  if (!place) return false;
  place(at);
  return true;
}
